import { useEffect, useState } from "react";

/**
 * InstallPrompt — One-tap "Install Moovin Forward" banner for the
 * FWA Expo booth. Purse holders leave the expo with the app pinned
 * to their home screen so Field Mode + triage stay one tap away.
 *
 * Behaviour by browser family:
 *   • Chrome / Edge / Android: listens for `beforeinstallprompt`,
 *     surfaces the native install dialog on tap.
 *   • iOS Safari (no beforeinstallprompt): shows a compact
 *     instructional tooltip ("Tap Share → Add to Home Screen").
 *
 * The banner:
 *   • Auto-hides when the app is already installed
 *     (`display-mode: standalone` or navigator.standalone on iOS).
 *   • Remembers dismissals in localStorage for 30 days so we don't
 *     nag a purse holder who explicitly closed it.
 */

const DISMISS_KEY = "mf:install-prompt:dismissed_at:v2";  // bumped 2026-02-12 to clear stale 30-day dismissals from v1 key
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;           // 7 days (was 30 — cut for faster re-invitation)

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (typeof navigator !== "undefined" && navigator.standalone) return true; // iOS
  return false;
};

const wasRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - parseInt(raw, 10) < DISMISS_TTL_MS;
  } catch { return false; }
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [inAppBrowser, setInAppBrowser] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Detect in-app webviews (Google Messages RCS, Facebook, Instagram,
    // WhatsApp, TikTok, LinkedIn). These CANNOT install PWAs at the OS
    // level — user must open the link in real Chrome/Safari first.
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const isInApp =
      /FBAN|FBAV|Instagram|Line|MicroMessenger|Twitter|TikTok|LinkedInApp/i.test(ua) ||
      // Google Messages RCS in-app browser and generic Android WebViews
      (/Android/i.test(ua) && /wv|; wv\)/i.test(ua));
    if (isInApp) {
      setInAppBrowser(true);
      setVisible(true);
      return;
    }

    if (isIOS()) {
      setIos(true);
      setVisible(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    // In-app webview → open the URL in the real browser via clipboard
    // + a system-share intent so the user lands in Chrome/Safari where
    // install actually works.
    if (inAppBrowser) {
      const currentUrl = window.location.href;
      // Try the Web Share API first (Android system share sheet includes "Open in Chrome")
      if (navigator.share) {
        try {
          await navigator.share({ title: "Moovin Forward", url: currentUrl });
          return;
        } catch { /* user cancelled — fall through */ }
      }
      // Fallback: copy the URL and tell them to paste it into Chrome
      try {
        await navigator.clipboard.writeText(currentUrl);
        alert("Link copied. Open Chrome or Safari, paste the link, then install.");
      } catch {
        alert("Open this page in Chrome or Safari (not inside Messages) to install the app.");
      }
      return;
    }
    if (ios) { setShowIosHelp(true); return; }
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch { /* user cancelled — no-op */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt-banner"
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: 16,
        zIndex: 9998,
        maxWidth: "min(560px, calc(100vw - 24px))",
        width: "100%",
        padding: "12px 14px",
        background: "rgba(3, 11, 26, 0.96)",
        border: "1px solid rgba(0, 255, 255, 0.35)",
        borderRadius: 12,
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 255, 255, 0.12)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <div
        aria-hidden
        style={{
          flexShrink: 0,
          width: 40, height: 40, borderRadius: 8,
          background: "rgba(0,255,255,0.08)",
          border: "1px solid rgba(0,255,255,0.25)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <img src="/logo-192.png" alt="" style={{ width: 30, height: 30, borderRadius: 4 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.25 }}>
          {inAppBrowser ? "Open in Chrome to Install" : "Install Moovin Forward"}
        </div>
        <div style={{ color: "#B4BEC8", fontSize: "0.78rem", lineHeight: 1.35, marginTop: 2 }}>
          {inAppBrowser
            ? <>This link opened inside a chat app. Tap <b style={{ color: "#00FFFF" }}>Open</b> to switch to Chrome or Safari — install works there.</>
            : showIosHelp
              ? <>Tap the <b style={{ color: "#00FFFF" }}>Share</b> icon (square with ↑) below → scroll → <b style={{ color: "#00FFFF" }}>Add to Home Screen</b> → <b style={{ color: "#00FFFF" }}>Add</b>.</>
              : ios
                ? <>Tap <b style={{ color: "#00FFFF" }}>How</b> for the 3-step install.</>
                : <>One tap to pin the app — triage, Field Mode, and offline capture stay one tap away.</>}
        </div>
      </div>
      <button
        type="button"
        data-testid="install-prompt-install-btn"
        onClick={install}
        style={{
          flexShrink: 0,
          background: "#00FFFF",
          color: "#030B1A",
          border: "none",
          borderRadius: 8,
          padding: "8px 14px",
          fontWeight: 800,
          fontSize: "0.82rem",
          letterSpacing: "0.02em",
          cursor: "pointer",
        }}
      >
        {inAppBrowser ? "Open" : ios ? "How" : "Install"}
      </button>
      <button
        type="button"
        aria-label="Dismiss install prompt"
        data-testid="install-prompt-dismiss-btn"
        onClick={dismiss}
        style={{
          flexShrink: 0,
          background: "transparent",
          color: "rgba(180,190,200,0.7)",
          border: "1px solid rgba(180,190,200,0.2)",
          borderRadius: 8,
          width: 28, height: 28,
          fontSize: "1rem",
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );
};

export default InstallPrompt;
